<script lang="ts">
  import { trackerState, type OpenEvent } from "../tracker/store.svelte";

  // Pixel + opens dérivés du store. Réactif : si un nouveau push arrive
  // pendant qu'on regarde le détail, la timeline se met à jour.
  const allOpens = $derived(
    trackerState.opens.filter((o) => o.pixel_id === trackerState.detailPixelId),
  );
  const pixelInfo = $derived(allOpens[0] ?? null);

  function close() {
    trackerState.detailPixelId = null;
  }

  // Esc ferme la modale (desktop)
  function onKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") close();
  }

  function formatDateTime(unixSec: number): string {
    const d = new Date(unixSec * 1000);
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  function formatDate(unixSec: number): string {
    return new Date(unixSec * 1000).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }

  function flagEmoji(country: string | null): string {
    if (!country || country.length !== 2) return "";
    const A = 0x1f1e6 - 0x41;
    return String.fromCodePoint(country.charCodeAt(0) + A, country.charCodeAt(1) + A);
  }

  /**
   * Heuristique légère sur le User-Agent. Pas de bibli — on affiche juste
   * un raccourci utile pour distinguer un humain d'un proxy.
   */
  function classifyUA(ua: string | null): string {
    if (!ua) return "unknown";
    const lower = ua.toLowerCase();
    // Proxies image connus (= probablement pas un vrai open par un humain)
    if (lower.includes("googleimageproxy")) return "Gmail proxy 🤖";
    if (lower.includes("yahoomailproxy")) return "Yahoo proxy 🤖";
    if (lower.includes("ggpht.com")) return "Google proxy 🤖";
    if (lower.includes("apple-mail")) return "Apple Mail 📩";
    if (lower.includes("outlook")) return "Outlook 📩";
    // Browsers — ouverture Gmail web typiquement
    if (lower.includes("iphone")) return "iPhone 📱";
    if (lower.includes("ipad")) return "iPad 📱";
    if (lower.includes("android")) return "Android 📱";
    if (lower.includes("mac os")) return "Mac 💻";
    if (lower.includes("windows")) return "Windows 💻";
    return "browser";
  }
</script>

<svelte:window onkeydown={onKeydown} />

{#if trackerState.detailPixelId && pixelInfo}
  <div
    class="overlay"
    onclick={close}
    role="presentation"
  >
    <div
      class="sheet"
      role="dialog"
      aria-modal="true"
      aria-label="Détail des ouvertures"
      tabindex="-1"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => e.stopPropagation()}
    >
      <header>
        <button class="close" onclick={close} aria-label="Fermer">×</button>
        <div class="who">
          {pixelInfo.recipient_name ?? pixelInfo.recipient_email}
        </div>
        {#if pixelInfo.recipient_name}
          <div class="email muted">{pixelInfo.recipient_email}</div>
        {/if}
        <div class="subject">{pixelInfo.subject ?? "(sans objet)"}</div>
        <div class="sent muted">
          Envoyé le {formatDate(pixelInfo.sent_at)}
        </div>
      </header>

      <div class="stats">
        <div class="stat">
          <div class="num">{allOpens.length}</div>
          <div class="lbl">{allOpens.length > 1 ? "ouvertures" : "ouverture"}</div>
        </div>
        <div class="stat">
          <div class="num">{new Set(allOpens.map((o) => o.country).filter(Boolean)).size}</div>
          <div class="lbl">pays</div>
        </div>
        <div class="stat">
          <div class="num">{new Set(allOpens.map((o) => o.city).filter(Boolean)).size}</div>
          <div class="lbl">villes</div>
        </div>
      </div>

      <h3>Timeline</h3>
      <ul class="timeline">
        {#each allOpens as o (o.id)}
          <li>
            <div class="time">{formatDateTime(o.opened_at)}</div>
            <div class="loc">
              {flagEmoji(o.country)} {o.city ?? o.country ?? "—"}
              {#if o.region && o.city && o.region !== o.city}
                <span class="muted small">({o.region})</span>
              {/if}
            </div>
            <div class="ua small muted">{classifyUA(o.user_agent)}</div>
          </li>
        {/each}
      </ul>
    </div>
  </div>
{/if}

<style>
  .overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 100;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    /* Sur desktop, centre vertical */
    @media (min-width: 640px) {
      align-items: center;
      padding: 1rem;
    }
  }
  .sheet {
    background: var(--bg);
    width: 100%;
    max-width: 560px;
    max-height: 90vh;
    overflow-y: auto;
    border-radius: 16px 16px 0 0;
    padding: 1.5rem;
    /* Slide up animation */
    animation: slideUp 0.2s ease-out;

    @media (min-width: 640px) {
      border-radius: 16px;
    }
  }
  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }

  header {
    border-bottom: 1px solid var(--border-subtle);
    padding-bottom: 1rem;
    margin-bottom: 1rem;
    position: relative;
  }
  .close {
    position: absolute;
    top: -8px;
    right: -8px;
    background: transparent;
    border: none;
    font-size: 1.8rem;
    cursor: pointer;
    color: var(--fg-subtle);
    line-height: 1;
    padding: 0 8px;
  }
  .close:hover { color: var(--fg); }

  .who {
    font-weight: 600;
    font-size: 1.1rem;
    color: var(--fg);
    padding-right: 30px;
  }
  .email {
    font-size: 0.85rem;
    margin-top: 2px;
  }
  .subject {
    margin-top: 8px;
    font-size: 0.95rem;
    color: var(--fg-muted);
  }
  .sent {
    margin-top: 4px;
    font-size: 0.78rem;
  }

  .stats {
    display: flex;
    gap: 1rem;
    padding: 1rem 0;
    border-bottom: 1px solid var(--border-subtle);
    margin-bottom: 1rem;
  }
  .stat {
    text-align: center;
    flex: 1;
  }
  .stat .num {
    font-size: 1.8rem;
    font-weight: 600;
    color: var(--accent);
    line-height: 1;
  }
  .stat .lbl {
    font-size: 0.78rem;
    color: var(--fg-subtle);
    margin-top: 4px;
  }

  h3 {
    font-size: 0.85rem;
    font-weight: 600;
    margin: 0 0 0.6rem;
    color: #57534e;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .timeline {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .timeline li {
    padding: 10px 12px;
    background: white;
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
  }
  .time {
    font-size: 0.78rem;
    color: var(--fg-subtle);
    font-variant-numeric: tabular-nums;
  }
  .loc {
    font-size: 0.95rem;
    color: var(--fg);
    margin-top: 2px;
  }
  .ua {
    margin-top: 2px;
  }
  .small { font-size: 0.78rem; }
  .muted { color: var(--fg-subtle); }

  @media (prefers-color-scheme: dark) {
    .sheet { background: var(--fg); }
    .who, .loc, .close:hover { color: var(--bg); }
    .subject { color: var(--border); }
    .muted, .time { color: var(--fg-subtle); }
    h3 { color: var(--border); }
    header, .stats { border-color: #292524; }
    .timeline li {
      background: #292524;
      border-color: var(--fg-muted);
    }
  }
</style>
